import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import Types from "../Helpers/Interfaces/ProductsType";
import {
  InventoryLogsResponse,
  InventoryResponse,
  Pagination,
} from "../Helpers/Interfaces/InventoryType";

// Define types for the API
export interface ProductData {
  name: string;
  description: string;
  price: string;
  categoryId: string;
  isActive: boolean;
  _id?: string;
}

export interface ProductResponse {
  _id: string;
  name: string;
  description: string;
  price: string;
  categoryId: {
    _id: string;
    name: string;
  };
  image: string;
  isActive: boolean;
  productImage: string;
  ratingValue: string;
  productName: string;
  isCustomMapping?: boolean;
}

export interface ProductListResponse {
  data: ProductResponse[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
}

export interface SearchResponse {
  pagination: any;
  products: ProductResponse[];
}

export interface ProductListParams {
  page: number;
  pageSize: number;
  filters?: any[];
  sortBy?: string;
  sortOrder?: string;
  search?: string;
}

export interface UpdateProductRequest {
  id?: string;
  data: any;
}

export interface fileResponseModel {
  status: string;
  statusCode: number;
  message: string;
  errors: null;
}

export interface ProductsLogsResponse {
  status: string;
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  data: ProductsLogsData;
}

export interface ProductsLogsData {
  _id: string;
  fileName: string;
  filePath: string;
  createdAt: Date;
  type?: string;
  allRecords: RecordsList; // NEW: All records combined with a source type
}

export interface RecordsList {
  supplierId?: string;
  supplierName?: string;
  productId?: string;
  productsku?: string;
  productName?: string;
  productDenominationId?: string;
  productDenominationsku?: string;
  executionType?: string;
  productType?: string;
  pins: PinData[];
}

export interface PinData {
  batch: string;
  pinStatus: string;
  source: string;
  type: string;
  isActive: boolean;
  isDelete: boolean;
  error?: string;
  row?: number;
}

export interface uploadMultipleFiles{
  data: any[];
}

export interface GetAllS3UploadedFilesParams {
  page?: number;
  pageSize?: number;
  bucketName?: string;
  folder?: string;
  createdBy?: string;
}





// Create the API slice
export const ProductService = createApi({
  reducerPath: "Products",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ""),
  tagTypes: ["Products"],
  endpoints: (builder) => ({
    getProducts: builder.query<ProductListResponse, ProductListParams>({
      query: (params) => ({
        url: `${ApiPath.getAllProducts}?page=${params.page}&pageSize=${params.pageSize}`,
        method: "POST",
        body: {
          filters:
            params?.filters && params?.filters.length > 0
              ? params?.filters
              : [{ key: "isDelete", value: false }],
          sortBy: params?.sortBy || "createdAt",
          sortOrder: params?.sortOrder || "desc",
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
        },
      }),
      providesTags: ["Products"],
    }),

    searchProducts: builder.query<ProductResponse[], string>({
      query: (searchString) => ({
        url: `${ApiPath.searchProducts}${searchString}`+`${!searchString ? '&page=1&limit=1' : ""}`,
        method: "GET",
      }),
      transformResponse: (response: any) => {
        return response.products;
      },
    }),

    productSearchForAdmin: builder.query<Types.SearchProductResponse, string>({
      query: (searchString) => ({
        url: `${ApiPath.productSearch}${searchString.length > 0 ? `?name=${searchString}`: ""}`,
        method: "GET",
      }),
    }),

    checkProductFieldIsDuplicateOrNot: builder.query<
      Types.SearchProductResponse,
      string
    >({
      query: (searchString) => ({
        url: `${ApiPath.checkProductFieldIsDuplicateOrNot}${searchString}`,
        method: "GET",
      }),
    }),

    checkCountryMapping: builder.query<
      { status: string; hasMapping: boolean },
      { productId: string; countryCode: string }
    >({
      query: ({ productId, countryCode }) => ({
        url: `${ApiPath.checkCountryMapping}/${productId}/${countryCode}`,
        method: "GET",
      }),
    }),

    createAndUpdateProduct: builder.mutation<
      ProductResponse,
      UpdateProductRequest
    >({
      query: (payload) => {
        // Check if this is an update request (has an id)
        if ("id" in payload) {
          return {
            url: `${ApiPath.updateProducts}/${payload.id}`,
            method: "PUT",
            body: payload.data,
          };
        }
        // This is a create request
        return {
          url: ApiPath.createProducts,
          method: "POST",
          body: payload.data,
        };
      },
      invalidatesTags: ["Products"],
    }),

    editProduct: builder.mutation<ProductResponse, UpdateProductRequest>({
      query: (payload) => ({
        url: `${ApiPath.editProduct}/${payload.id}`,
        method: "PUT",
        body: payload.data,
      }),
      invalidatesTags: ["Products"],
    }),

    deleteProducts: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ApiPath.deleteProducts}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Products"],
    }),

    getProductDetails: builder.query<Types.ProductDetails, string>({
      query: (id) => ({
        url: `${ApiPath.getProductsDetails}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: Types.ProductDetails }) =>
        response.data,
      providesTags: ["Products"],
    }),

    getProductImages: builder.query({
      query: ({ page = 1, pageSize = 10 }) => ({
        url: `${ApiPath.getProductImages}`,
        method: "GET",
        params: { page, pageSize }, // Send as query parameters
      }),
      transformResponse: (response: { data: any }) => response,
      providesTags: ["Products"],
    }),

    UploadProductSheet: builder.mutation<
      fileResponseModel,
      { file: File; id?: string }
    >({
      query: ({ file, id }) => {
        const formData: any = new FormData();
        formData.append("file", file);
        return {
          url: id
            ? `${ApiPath.productSheetUpload}/${id}`
            : ApiPath.productSheetUpload,
          method: "POST",
          body: formData,
          extraOptions: {
            fileUpload: true,
          },
        };
      },
    }),

    /**
     * Bulk upload of images for the product import sheet.
     *
     * Separate from uploadmultiple because the server keeps these file names
     * exactly as given -- that is the whole point, since the sheet refers to an
     * image by name -- while the ordinary product upload uniquifies them.
     */
    uploadImportImages: builder.mutation<any, { images: File[] }>({
      query: ({ images }) => {
        const formData = new FormData();
        images.forEach((image) => {
          formData.append("files", image);
        });
        return {
          url: ApiPath.uploadImportImages,
          method: "POST",
          body: formData,
          extraOptions: {
            fileUpload: true,
          },
        };
      },
    }),

    uploadmultiple: builder.mutation<any, { images: File[]; id?: string }>({
      query: ({ images, id }) => {
        const formData = new FormData();

        images.forEach((image) => {
          formData.append("file", image); // or use 'file[]' if backend requires array
        });

        if (id) {
          formData.append("id", id);
        }

        return {
          url: ApiPath.uploadmultiple,
          method: "POST",
          body: formData,
          extraOptions: {
            fileUpload: true,
          },
        };
      },
    }),

    checkAllFileProcessResult: builder.mutation<
      Types.ImportProductResponseModel,
      ProductListParams
    >({
      query: (params) => ({
        url: ApiPath.allFileProcessResult,
        method: "POST",
        body: {
          filters: params.filters,
          search: params.search || "",
          sortBy: "updatedAt",
          sortOrder: "desc",
          page: params.page || 1,
          pageSize: params.pageSize || 10,
        },
      }),
    }),

    productWithSupplierMapUpdateApi: builder.mutation<
      Types.ImportProductResponseModel,
      { _id: string; reqBody: any }
    >({
      query: (params) => ({
        url: `${ApiPath.productWithSupplierMapUpdate}/${params?._id}`,
        method: "PUT",
        body: params.reqBody,
      }),
    }),

    // getSupplierMappedWithoutCustom: builder.query<any, string>({
    //   query: (params) => ({
    //     url: `${ApiPath.getSupplierMappedWithoutCustom}/${params}`,
    //     method: "GET",
    //   }),
    // }),

    /**Supplier Provider Product Search */
    // supplierProviderProductSearch: builder.query<any, { productName: string; supplierId: string, countryIso?: string, subSupplierId?: string }>({
    //   query: (params) => ({
    //     url: `${ApiPath.supplierProviderProductSearch}?productName=${params.productName}&supplierId=${params.supplierId}&subSupplierId=${params.subSupplierId}${params.countryIso ? `&countryIso=${params.countryIso}` : ""}`,
    //     method: "GET",
    //   }),
    // }),
    supplierProviderProductSearch: builder.query<
      any,
      {
        productName: string;
        supplierId: string;
        countryIso?: string;
        subSupplierId?: string;
        page?: number;
        pageSize?: number;
      }
    >({
      query: (params) => {
        // Encoded: the search term is typed by the user, so an "&" or "+" in a
        // provider name would otherwise truncate or corrupt the query string.
        const qs = new URLSearchParams();
        qs.set("productName", params.productName ?? "");
        qs.set("supplierId", params.supplierId ?? "");
        if (params.subSupplierId) qs.set("subSupplierId", params.subSupplierId);
        if (params.countryIso) qs.set("countryIso", params.countryIso);
        if (params.page) qs.set("page", String(params.page));
        if (params.pageSize) qs.set("pageSize", String(params.pageSize));
        return {
          url: `${ApiPath.supplierProviderProductSearch}?${qs.toString()}`,
          method: "GET",
        };
      },
    }),

    /**@sandeep_chnaged_below_API */
    allSupplierProducts: builder.mutation<any, { providerCode: string }>({
      query: (providerCode) => ({
        url: ApiPath.allSupplierProducts,
        method: "POST",
        body: {
          filters: [
            { key: "providerCode", value: `${providerCode}` },
            { key: "isActive", value: true },
          ],
        },
      }),
    }),

    getproductfilehistory: builder.mutation<any, Pagination>({
      query: (params) => ({
        url: ApiPath.productfilehistory,
        method: "POST",
        body: {
          filters: params.filters,
          search: params.search || "",
          sortBy: "uploadedAt",
          sortOrder: "desc",
          page: params.page || 1,
          pageSize: params.pageSize || 10,
        },
      }),
    }),

    getProductRating: builder.query<any, string>({
      query: (params) => ({
        url: `${ApiPath.productRating}${params}`,
        method: "GET",
      }),
    }),

    getproductfilehistorylist: builder.mutation<any, any>({
      query: (reqBody) => ({
        url: ApiPath.productfilehistorylist,
        method: "POST",
        body: reqBody,
      }),
      transformResponse: (response: any): InventoryLogsResponse => {
        const {
          status,
          totalRecords,
          totalPages,
          currentPage,
          pageSize,
          data,
        } = response;

        const {
          validRecords,
          existingRecords,
          fileErrors,
          blockedRecords,
          ...baseData
        } = data ?? {};

        let allRecords: RecordsList = { pins: [] };
        let type = "";
        if (Array.isArray(validRecords) && validRecords?.length) {
          allRecords.pins = validRecords;
          type = "validRecords";
        } else if (Array.isArray(fileErrors) && fileErrors?.length) {
          type = "fileErrors";
          allRecords.pins = fileErrors;
        } else if (Array.isArray(existingRecords) && existingRecords?.length) {
          type = "existingRecords";
          allRecords.pins = existingRecords;
        }

        return {
          status,
          totalRecords,
          totalPages,
          currentPage,
          pageSize,
          data: {
            _id: baseData._id,
            fileName: baseData.fileName,
            filePath: baseData.filePath,
            createdAt: baseData.createdAt || null,
            allRecords,
            type,
          },
        };
      },
    }),

    uploadSingleFile: builder.mutation<any, FormData>({
      query: (formData) =>{
        return{
        url: ApiPath.uploadSingleFile,
        method: "POST",
        body: formData,
      }
      }
    }),

    uploadMultipleFiles: builder.mutation<any, File[]>({
      query: (files) => {
        const formData = new FormData();
        
        // Append each file to FormData with 'files' key
        files.forEach((file) => {
          formData.append("files", file);
        });

        return {
          url: ApiPath.uploadMultipleFile,
          method: "POST",
          body: formData,
          extraOptions: {
            fileUpload: true,
          },
        };
      },
    }),

    deleteSingleFile: builder.mutation<
      void,
      string | { recordId?: string; url?: string; key?: string }
    >({
      query: (payload) => {
        const buildUrl = () => {
          // String payload: decide by prefix
          if (typeof payload === "string") {
            if (/^https?:\/\//i.test(payload) || payload.startsWith("s3://") || payload.startsWith("data:")) {
              return `${ApiPath.deleteSingleFile}?url=${encodeURIComponent(payload)}`;
            }
            const sanitizedKey = (payload || "").replace(/^\/+/, "");
            return `${ApiPath.deleteSingleFile}/${sanitizedKey}`;
          }

          // Object payload: prefer URL, fallback to recordId/key
          const fileUrl = payload.url;
          if (fileUrl) {
            return `${ApiPath.deleteSingleFile}?url=${encodeURIComponent(fileUrl)}`;
          }
          const recordId = payload.recordId || payload.key || "";
          const sanitizedKey = (recordId || "").replace(/^\/+/, "");
          return `${ApiPath.deleteSingleFile}/${sanitizedKey}`;
        };

        return {
          url: buildUrl(),
          method: "DELETE",
        };
      },
    }),

    getAllS3UploadedFiles: builder.mutation<any, GetAllS3UploadedFilesParams | void>({
      query: (params) => {
        const {
          page = 1,
          pageSize = 10,
          bucketName,
          folder,
          createdBy,
        } = params || {};

        const queryParams: Record<string, string | number> = {
          page,
          pageSize,
        };

        if (bucketName) queryParams.bucketName = bucketName;
        if (folder) queryParams.folder = folder;
        if (createdBy) queryParams.createdBy = createdBy;

        return {
        url: ApiPath.getAllS3ImageFiles,
        method: "GET",
        params: queryParams,
        };
      },
    }),

    // =============================================================================
    // PRICING & DISCOUNT ENDPOINTS
    // =============================================================================

    // Validate discount configuration with loss warnings
    validateDiscount: builder.mutation<any, {
      productId: string;
      discountApplyTo: 'product' | 'denomination';
      discountType: 'percentage' | 'flat';
      productDiscount?: { onlinePayment: number; bankTransfer: number };
      denominations?: Array<{
        productDenominationsku: string;
        denomination: number;
        discount?: {
          discountType: 'percentage' | 'flat';
          onlinePayment: number;
          bankTransfer: number;
        };
      }>;
    }>({
      query: (payload) => ({
        url: '/product/validate-discount',
        method: 'POST',
        body: payload,
      }),
    }),

    // Get bulk pricing for all denominations of a product
    getBulkPricing: builder.query<any, string>({
      query: (productId) => ({
        url: `/product/${productId}/bulk-pricing`,
        method: 'GET',
      }),
    }),

    // Update product discount configuration
    updateProductDiscount: builder.mutation<any, {
      id: string;
      discountApplyTo: 'product' | 'denomination';
      discountType: 'percentage' | 'flat';
      productDiscount?: { onlinePayment: number; bankTransfer: number };
      productDenominations?: any[];
      forceUpdate?: boolean;
    }>({
      query: (payload) => ({
        url: `/product/${payload.id}/discount`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['Products'],
    }),

    // =============================================================================
    // BULK DISCOUNT ENDPOINTS
    // =============================================================================

    // Preview bulk discount impact across multiple products
    bulkDiscountPreview: builder.mutation<any, {
      productIds: string[];
      discountApplyTo: 'product' | 'denomination';
      discountType: 'percentage' | 'flat';
      productDiscount?: { onlinePayment: number; bankTransfer: number };
      denominationDiscounts?: Array<{
        productDenominationsku: string;
        discount: {
          discountType: 'percentage' | 'flat';
          onlinePayment: number;
          bankTransfer: number;
        };
      }>;
    }>({
      query: (payload) => ({
        url: '/product/bulk-discount-preview',
        method: 'POST',
        body: payload,
      }),
    }),

    // Apply bulk discount to multiple products
    bulkUpdateDiscount: builder.mutation<any, {
      productIds: string[];
      discountApplyTo: 'product' | 'denomination';
      discountType: 'percentage' | 'flat';
      productDiscount?: { onlinePayment: number; bankTransfer: number };
      forceUpdate?: boolean;
    }>({
      query: (payload) => ({
        url: '/product/bulk-discount',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['Products'],
    }),

  }),
});

// Export hooks for usage in components
export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useCreateAndUpdateProductMutation,
  useDeleteProductsMutation,
  useLazyGetProductDetailsQuery,
  useLazySearchProductsQuery,
  useUploadProductSheetMutation,
  useCheckAllFileProcessResultMutation,
  useLazyProductSearchForAdminQuery,

  useLazySupplierProviderProductSearchQuery,
  useAllSupplierProductsMutation,

  useProductWithSupplierMapUpdateApiMutation,
  // useGetSupplierMappedWithoutCustomQuery,
  useGetproductfilehistoryMutation,
  useGetproductfilehistorylistMutation,

  useEditProductMutation,
  useUploadmultipleMutation,
  useUploadImportImagesMutation,
  useGetProductImagesQuery,
  useLazyGetProductRatingQuery,

  useLazyCheckProductFieldIsDuplicateOrNotQuery,
  useLazyCheckCountryMappingQuery,

  useUploadSingleFileMutation,
  useUploadMultipleFilesMutation,
  useDeleteSingleFileMutation,
  useGetAllS3UploadedFilesMutation,

  // Pricing & Discount
  useValidateDiscountMutation,
  useLazyGetBulkPricingQuery,
  useUpdateProductDiscountMutation,

  // Bulk Discount
  useBulkDiscountPreviewMutation,
  useBulkUpdateDiscountMutation,

} = ProductService;
